import { auth } from "@fynd-onshelf/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import { z } from "zod";

import { AuthShell } from "../components/auth/AuthShell.js";
import { PasswordInput } from "../components/auth/PasswordInput.js";
import { Button } from "../components/ui/button.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form.js";
import { Input } from "../components/ui/input.js";
import { useLoginMutation, useSignupResendMutation } from "../lib/api/auth.js";
import { useInvitationLookupQuery } from "../lib/api/invitations.js";
import {
  authDestination,
  clearAuthReturnTo,
  invitationTokenFromReturnTo,
  rememberAuthReturnTo,
  safeAuthReturnTo,
} from "../lib/auth/return-to.js";
import { APIError } from "../lib/http.js";
import { useAuth } from "../providers/auth-provider.js";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  // Single source of truth shared with the API contract (min 8 / max 256).
  password: auth.passwordSchema,
});
type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const returnTo = safeAuthReturnTo(searchParams.get("returnTo"));
  const invitation = useInvitationLookupQuery(invitationTokenFromReturnTo(returnTo) ?? null);
  // Initialize the form with the lookup result so prefill cannot race typing.
  if (invitation.isLoading && !invitation.isFetched)
    return <div className="p-8 text-sm text-muted-foreground">Loading invitation…</div>;
  return (
    <LoginForm
      key={returnTo}
      returnTo={returnTo}
      invitedEmail={invitation.data?.invitedEmail ?? ""}
    />
  );
}

function LoginForm({
  returnTo,
  invitedEmail,
}: {
  returnTo: string | null;
  invitedEmail: string;
}): JSX.Element {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const loginMut = useLoginMutation();
  const resendMut = useSignupResendMutation();

  // After login (or if already authed) leave the public router and enter the
  // app via a full navigation. Invitations are root routes, and take priority
  // over the current/default company just like authenticated deep links.
  useEffect(() => {
    if (loading || !user) return;
    clearAuthReturnTo();
    window.location.replace(authDestination(returnTo, user.tenantCode));
  }, [loading, user, returnTo]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: invitedEmail, password: "" },
    mode: "onSubmit",
  });

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (user) return <div className="p-8 text-sm text-muted-foreground">Redirecting…</div>;

  const submitError = loginMut.error
    ? loginMut.error instanceof APIError
      ? loginMut.error.displayDetail
      : (loginMut.error as Error).message
    : null;

  const successBanner =
    searchParams.get("message") === "password-reset"
      ? "Password reset. Please sign in with your new password."
      : null;

  return (
    <AuthShell title="Sign in" subtitle="Sign in to access your Onshelf workspace">
      {successBanner ? (
        <p className="mb-4 rounded-md border border-border bg-muted/50 p-2 text-center text-sm text-foreground">
          {successBanner}
        </p>
      ) : null}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => {
            rememberAuthReturnTo(returnTo, values.email);
            resendMut.reset();
            loginMut.mutate(values);
          })}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email ID</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="Enter Email ID"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="current-password"
                    placeholder="Enter Password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {submitError ? (
            <p className="text-sm text-destructive" role="alert">
              {submitError}
            </p>
          ) : null}
          {loginMut.error?.problem?.code === "EMAIL_NOT_VERIFIED" ? (
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={resendMut.isPending}
                onClick={() => {
                  rememberAuthReturnTo(returnTo, form.getValues("email"));
                  resendMut.mutate({
                    email: form.getValues("email"),
                    invitationToken: invitationTokenFromReturnTo(returnTo),
                  });
                }}
              >
                {resendMut.isPending ? "Sending…" : "Resend verification email"}
              </Button>
              {resendMut.isSuccess ? (
                <p className="text-sm text-muted-foreground">
                  Check your email to verify your account and continue.
                </p>
              ) : null}
              {resendMut.error ? (
                <p role="alert" className="text-sm text-destructive">
                  {resendMut.error.displayDetail}
                </p>
              ) : null}
            </div>
          ) : null}
          <Button
            type="submit"
            size="lg"
            className="mt-1 w-full rounded-full"
            disabled={loginMut.isPending}
          >
            {loginMut.isPending ? "Signing in…" : "Sign in"}
          </Button>
          <div className="mt-1 flex flex-col gap-2 text-center text-sm">
            <Link to="/forgot-password" className="font-medium text-primary hover:underline">
              Forgot password?
            </Link>
            <div className="text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link
                to={
                  searchParams.get("returnTo")
                    ? `/signup?returnTo=${encodeURIComponent(searchParams.get("returnTo")!)}`
                    : "/signup"
                }
                className="font-medium text-primary hover:underline"
              >
                Sign up
              </Link>
            </div>
          </div>
        </form>
      </Form>
    </AuthShell>
  );
}
